/**
 * dynamaxDex — the per-species Dynamax view's data contract.
 *
 * The Pokémon detail page grows a second view ("Dynamax page") for every
 * species in the Dynamax roster: the same tile grid, scoped to the copies a
 * Max Battle can produce. Six tiles:
 *
 *   - DEFAULT (a normal Dynamax catch) is the existing Dynamax dex row —
 *     variant "dynamax", base species key. Nothing new is invented for it, so
 *     the Dex carousel's Dynamax tab, the Bulk Update matrix and this page all
 *     read and write the same cell.
 *   - SHINY / PERFECT / LUCKY / XXL / XXS are Dynamax-scoped copies of the
 *     full-dex variants. They are stored as FORM rows under those variants
 *     (`shiny:6:dynamax` …) using the reserved form id below. That reuses the
 *     whole (variant, dexNumber, formId) machinery for free: server sync
 *     round-trips formId untouched, grids don't double-count form rows, and
 *     setManual's form cascade makes a Shiny Dynamax catch light the base
 *     Shiny and Main cells, which is right: a shiny Dynamax Charizard IS a
 *     shiny Charizard.
 *
 * The one cascade the form engine cannot know about is Dynamax itself: any
 * Dynamax-scoped catch implies a Dynamax catch, so toggling a form tile ON
 * also ticks the DEFAULT row (which in turn links to Main, like every Dynamax
 * add). One-directional, like every cascade in the store: removals never
 * propagate.
 */
import {
  getDexEntryState,
  setManualWithLinks,
  toggleManual,
} from "./dexStateStore";
import { isCustomDexLinkageEnabled } from "./customDexLinkagePref";
import { variantSupportsSpecies } from "./speciesVariants";
import type { DexEntryState } from "./dexStateStore";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/**
 * Reserved form id for Dynamax-scoped dex rows. Not a catalogue form on
 * purpose: FORM_GROUPS knows costumes and real forms, and every consumer of
 * unknown form ids fails closed (hidden), so this never leaks into the forms
 * grids while still riding the form-row storage, sync and cascade paths.
 */
export const DYNAMAX_FORM_ID = "dynamax";

/**
 * The Dynamax page's tile order. "dynamax" is the DEFAULT tile (a normal
 * Dynamax catch); the rest follow the detail page's card order (Perfect before
 * Lucky, XXL before XXS, matching Pokémon GO's own card and page one).
 */
export const DYNAMAX_PAGE_VARIANTS: readonly PokedexVariant[] = [
  "dynamax",
  "shiny",
  "perfect",
  "lucky",
  "xxl",
  "xxs",
];

/** True when the species has a Dynamax form in GO (curated roster, portal
 * overrides included) and the detail page should offer the Dynamax view. */
export function speciesHasDynamax(dexNumber: number): boolean {
  return variantSupportsSpecies("dynamax", dexNumber);
}

/**
 * One-shot hand-off from the Dynamax dex grid: tapping a cell on the D-MAX
 * tab should land on the detail page with the Dynamax view already open, not
 * on page one with a second tap to make. The grid ARMS this right before it
 * navigates; the detail page CONSUMES it on the species it mounts (or swipes
 * to), so it fires exactly once and a later visit from anywhere else opens
 * page one as normal. Module state, same pattern as rememberDexScroll: the
 * hand-off only exists inside one in-app navigation.
 */
let pendingMaxView = false;

export function armDynamaxDetailView(): void {
  pendingMaxView = true;
}

/** Read-and-clear. False when nothing was armed. */
export function consumeDynamaxDetailView(): boolean {
  const v = pendingMaxView;
  pendingMaxView = false;
  return v;
}

/** The stored row behind one Dynamax-page tile (null = never recorded). */
export function getDynamaxTileEntry(
  variant: PokedexVariant,
  dexNumber: number,
): DexEntryState | null {
  return variant === "dynamax"
    ? getDexEntryState("dynamax", dexNumber)
    : getDexEntryState(variant, dexNumber, DYNAMAX_FORM_ID);
}

/**
 * Toggle one Dynamax-page tile, the Manual Edit tap. Returns true when the
 * write persisted (the page keeps the store's durability contract).
 *
 * DEFAULT mirrors the page-one D-MAX tile exactly: adding goes through
 * setManualWithLinks so the Dynamax dex's Main linkage applies; removing is a
 * plain toggle. A variant tile toggles its form row, and when that lands on
 * "present" it also ensures the DEFAULT row: you cannot own a Shiny Dynamax
 * without owning a Dynamax.
 */
export function toggleDynamaxTile(variant: PokedexVariant, dexNumber: number): boolean {
  if (variant === "dynamax") {
    const present = getDexEntryState("dynamax", dexNumber)?.state === "present";
    if (!present) {
      return setManualWithLinks("dynamax", dexNumber, "present", {
        propagate: isCustomDexLinkageEnabled(),
      });
    }
    return toggleManual("dynamax", dexNumber) != null;
  }
  const next = toggleManual(variant, dexNumber, DYNAMAX_FORM_ID);
  if (next?.state === "present") {
    const dmaxPresent = getDexEntryState("dynamax", dexNumber)?.state === "present";
    if (!dmaxPresent) {
      setManualWithLinks("dynamax", dexNumber, "present", {
        propagate: isCustomDexLinkageEnabled(),
      });
    }
  }
  return next != null;
}
